import PropTypes from "prop-types";
import { useCallback, useMemo, useRef, useState, useEffect, createContext } from "react";

// Helpers
import { getPayPlanPosition } from "@Common/helpers/PayPlans/positions";
import { isSimilarObject } from "@Common/helpers/Verifications/objects";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useErrors } from "@Common/hooks/Errors";
import { usePayPlanStore } from "@PayPlans/hooks/usePayPlans/usePayPlans";
import { useServices } from "@PayPlans/hooks/useServices/useServices";

const checkNewPayPlan = (selectedId = "") => selectedId.startsWith("new_");
const isSimilar = (previous, current) => isSimilarObject(previous, current);

const PayPlanContext = createContext();

function PayPlanProvider(props) {
	const {
		cacheManagement = {
			clearAbsoluteIndexes: [],
		},
		children = null,
		defaultSections = [],
		emptySelectionTitle = "Select a Pay Plan",
		instantCreation = false,
		locationId = "",
		position = "",
		positionType = "",
	} = props;

	const positionId = getPayPlanPosition(position);

	// Hooks
	const { actions, queries, mutations, subscriptions } = useServices(position, positionType);
	const { errors, getErrorKey, hasError, hasSomeError, addError, addErrors, clearError, clearErrors, removeError, removeErrors } =
		useErrors();

	// Authorization
	const { useAuth } = useAuthorization();
	const editable = useAuth(actions.editable);

	// States
	const [selected, setSelected] = useState(null);
	const [someNewPayPlan, setSomeNewPayPlan] = useState(false);
	const [defaultModel, setDefaultModel] = useState(null);
	const [isLoadingModel, setIsLoadingModel] = useState(false);
	const [isPayPlanLocked, setIsPayPlanLocked] = useState(false);
	const [isPayPlanAvailable, setIsPayPlanAvailable] = useState(false);
	const [selectionIds, setSelectionIds] = useState([]);
	const [isEditing, setIsEditing] = useState(false);

	// Complement update sections
	const updatedModel = useRef([]);
	// TODO: Remove when default component is updated
	const lastErrorDefaultModel = useRef(null);
	const [updatedSections, setUpdatedSections] = useState(null);

	const removeSection = useCallback((sections = [], section = "") => sections.filter((item) => item.section !== section), []);

	const setSelections = useCallback((id) => {
		setSelectionIds((prev) => [...new Set([...prev, id])]);
	}, []);

	const [model, setModel] = useState({ section: "", data: null });

	useMemo(() => {
		// TODO: Remove when default component is updated
		const isValidToSave =
			!hasSomeError &&
			!(positionId === "salesManager" && model.section === "default" && isSimilar(lastErrorDefaultModel.current, model.data));
		if (Boolean(model?.section) && isValidToSave) {
			const { section = "", data = null } = model || {};

			// Remove from the updated sections
			const sections = removeSection([...updatedModel.current], section);
			updatedModel.current = [...sections];

			// Check with the initial model if something changed
			const compare = defaultModel?.[section] || null;
			if (!isSimilar(compare, data)) {
				// Save to the temporal updated list
				updatedModel.current = [...sections, { section, data }];
			}
		}

		if (isValidToSave && updatedModel.current?.length) {
			const modelToUpdate = updatedModel?.current?.reduce((item, element) => {
				if (item) {
					item[element.section] = element.data;
				}
				return item;
			}, {});
			setUpdatedSections(modelToUpdate);
		}

		if (hasSomeError) {
			lastErrorDefaultModel.current = model.data;
		}
	}, [model, defaultModel, hasSomeError, removeSection]);

	const useSectionsErrors = useCallback(
		() => ({
			errors,
			getErrorKey,
			hasError,
			hasSomeError,
			clearErrors,
			addSectionError: addError,
			addSectionErrors: addErrors,
			removeSectionError: removeError,
			removeSectionErrors: removeErrors,
			clearSectionErrors: clearError,
		}),
		[errors, getErrorKey, hasError, hasSomeError, addError, addErrors, clearError, clearErrors, removeError, removeErrors]
	);

	const isNewPayPlan = useCallback(() => checkNewPayPlan(selected?._id), [selected]);

	const setInitialModel = useCallback((localModel = null) => {
		updatedModel.current = [];
		setUpdatedSections(null);
		setModel({ section: "", data: null });
		setDefaultModel(JSON.parse(JSON.stringify(localModel)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isValidModel = useMemo(() => !(isLoadingModel || hasSomeError), [isLoadingModel, hasSomeError]);

	// Store Context
	const store = usePayPlanStore({
		position,
		positionId,
		positionType,
		locationId,
		emptySelectionTitle,
		instantCreation,
		selected,
		setSelected,
		model,
		setModel,
		setInitialModel,
		defaultSections,
		updatedSections,
		setUpdatedSections,
		isPayPlanLocked,
		setIsPayPlanLocked,
		isPayPlanAvailable,
		setIsPayPlanAvailable,
		isLoadingModel,
		setIsLoadingModel,
		isNewPayPlan, // Used to know if the plan is new
		setSomeNewPayPlan,
		// Cache functions
		cacheManagement,
		useSectionsErrors,
		selectionIds,
		setSelections,
		isEditing,
		setIsEditing,
		isDefaultEditable: editable,
		isLoading: false,
		initialModel: defaultModel,
		isSomeNewPayPlan: someNewPayPlan, // Used to know if there is a new pay plan
		isValid: isValidModel, // Check if the pay plan is valid (check only for errors)
		readOnly: false, // Check for read-only cases
		services: { queries, mutations, subscriptions },
	});

	useEffect(() => {
		return () => {
			clearErrors();
			setIsLoadingModel(false);
			setIsPayPlanLocked(false);
			setIsPayPlanAvailable(false);
			setUpdatedSections(null);
			setIsEditing(false);
			setModel({ section: "", data: null });
			updatedModel.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected, clearErrors]);

	useEffect(() => {
		return () => {
			setSelected(null);
			setDefaultModel(null);
			setIsLoadingModel(false);
			setIsPayPlanLocked(false);
			setIsPayPlanAvailable(false);
			setUpdatedSections(null);
			setSelectionIds([]);
			setIsEditing(false);
			setModel({ section: "", data: null });
			updatedModel.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [position, locationId]);

	return <PayPlanContext.Provider value={store}>{children}</PayPlanContext.Provider>;
}

PayPlanProvider.propTypes = {
	cacheManagement: PropTypes.shape({
		clearAbsoluteIndexes: PropTypes.arrayOf(PropTypes.string),
	}),
	children: PropTypes.node,
	defaultSections: PropTypes.arrayOf(PropTypes.string),
	emptySelectionTitle: PropTypes.string,
	instantCreation: PropTypes.bool,
	locationId: PropTypes.string,
	position: PropTypes.string,
	positionType: PropTypes.string,
};

export { PayPlanContext, PayPlanProvider };
